import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './Layout';
import StatusInfo from '../components/StatusInfo';
import ListPager from '../components/ListPager';
import { referralLabel, REFERRAL_STATUS_OPTIONS } from '../utils/referralStatus';
import { fmtMoney } from '../utils/format';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const CLINIC_CABINET_URL = process.env.REACT_APP_CLINIC_CABINET_URL || 'http://127.0.0.1:3002';

const MONTHS = ['', 'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];

const STATUS_OPTIONS = REFERRAL_STATUS_OPTIONS;

function monthOptions() {
  const opts = [{ value: '', label: 'Все месяцы' }];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const label = d.toLocaleString('ru', { month: 'long', year: 'numeric' });
    opts.push({ value: `${d.getFullYear()}-${d.getMonth() + 1}`, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return opts;
}
const MONTH_OPTIONS = monthOptions();

const REF_LIMIT = 20;
const COM_LIMIT = 50;

function formatClinicId(id) { return 'CL-' + String(id).padStart(3, '0'); }

const BILLING_FIELDS = [
  ['legal_name', 'Юр. название'],
  ['inn', 'ИНН'],
  ['kpp', 'КПП'],
  ['ogrn', 'ОГРН/ОГРНИП'],
  ['legal_address', 'Юр. адрес'],
  ['bank_name', 'Банк'],
  ['bank_bik', 'БИК'],
  ['bank_account', 'Р/с'],
  ['bank_corr_account', 'К/с'],
  ['billing_email', 'Email для счетов'],
];
const BILLING_EMPTY = Object.fromEntries(BILLING_FIELDS.map(([k]) => [k, '']));

export default function ClinicDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [showDisconnect, setShowDisconnect] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [showHideConfirm, setShowHideConfirm] = useState(false);
  const [hideConfirmName, setHideConfirmName] = useState('');
  const [hideMsg, setHideMsg] = useState('');

  const key = localStorage.getItem('adminKey');
  const headers = { 'X-Admin-Key': key };
  const [impersonateBusy, setImpersonateBusy] = useState(false);

  const impersonate = async () => {
    setImpersonateBusy(true);
    try {
      const r = await axios.post(`${API}/admin/clinics/${id}/impersonate`, {}, { headers });
      window.open(`${CLINIC_CABINET_URL}/admin-session?token=${encodeURIComponent(r.data.token)}&name=${encodeURIComponent(r.data.name)}`, '_blank');
    } catch { /* тихо */ }
    setImpersonateBusy(false);
  };

  // ── Обзор ──
  const load = useCallback((from, to) => {
    const params = {};
    if (from && to) { params.date_from = from; params.date_to = to; }
    axios.get(`${API}/admin/clinics/${id}`, { headers: { 'X-Admin-Key': key }, params })
      .then(r => { setData(r.data); setError(''); })
      .catch(() => setError('Не удалось загрузить клинику'));
  }, [id, key]);

  useEffect(() => { load(); }, [load]);

  const apply = () => load(dateFrom, dateTo);
  const reset = () => { setDateFrom(''); setDateTo(''); load('', ''); };
  const isoDay = (d) => d.toISOString().split('T')[0];

  // ── Вкладка «Направления» ──
  const [refItems, setRefItems] = useState([]);
  const [refTotal, setRefTotal] = useState(0);
  const [refOffset, setRefOffset] = useState(0);
  const [refSearch, setRefSearch] = useState('');
  const [refStatus, setRefStatus] = useState('');
  const [refSort, setRefSort] = useState('bound_at');
  const [refOrder, setRefOrder] = useState('desc');

  const fetchRefs = useCallback(() => {
    const params = { search: refSearch || undefined, status: refStatus || undefined, sort: refSort, order: refOrder, limit: REF_LIMIT, offset: refOffset };
    axios.get(`${API}/admin/clinics/${id}/referrals`, { headers: { 'X-Admin-Key': key }, params })
      .then(r => { setRefItems(r.data.items); setRefTotal(r.data.total); });
  }, [id, key, refSearch, refStatus, refSort, refOrder, refOffset]);

  useEffect(() => {
    if (tab !== 'referrals') return;
    const t = setTimeout(fetchRefs, 250);
    return () => clearTimeout(t);
  }, [tab, fetchRefs]);

  const refToggleSort = (f) => { setRefOffset(0); if (refSort === f) setRefOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setRefSort(f); setRefOrder('asc'); } };
  const refArrow = (f) => (refSort !== f ? '' : refOrder === 'asc' ? ' ▲' : ' ▼');

  // ── Вкладка «Комиссии» ──
  const [comItems, setComItems] = useState([]);
  const [comTotal, setComTotal] = useState(0);
  const [comOffset, setComOffset] = useState(0);
  const [comSearch, setComSearch] = useState('');
  const [comMonth, setComMonth] = useState('');
  const [comSort, setComSort] = useState('period');
  const [comOrder, setComOrder] = useState('desc');

  const fetchComs = useCallback(() => {
    const params = { search: comSearch || undefined, sort: comSort, order: comOrder, limit: COM_LIMIT, offset: comOffset };
    if (comMonth) { const [y, m] = comMonth.split('-'); params.year = y; params.month = m; }
    axios.get(`${API}/admin/clinics/${id}/commissions`, { headers: { 'X-Admin-Key': key }, params })
      .then(r => { setComItems(r.data.items); setComTotal(r.data.total); });
  }, [id, key, comSearch, comMonth, comSort, comOrder, comOffset]);

  useEffect(() => {
    if (tab !== 'commissions') return;
    const t = setTimeout(fetchComs, 250);
    return () => clearTimeout(t);
  }, [tab, fetchComs]);

  const comToggleSort = (f) => { setComOffset(0); if (comSort === f) setComOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setComSort(f); setComOrder('asc'); } };
  const comArrow = (f) => (comSort !== f ? '' : comOrder === 'asc' ? ' ▲' : ' ▼');

  // ── Вкладка «Комиссии по операциям» (N-119) — по аналогии с вкладкой «Операции» на
  // странице врача, но отфильтровано по этой клинике (не по доктору).
  const [opItems, setOpItems] = useState([]);
  const [opTotal, setOpTotal] = useState(0);
  const [opOffset, setOpOffset] = useState(0);
  const [opSort, setOpSort] = useState('invoiced_at');
  const [opOrder, setOpOrder] = useState('desc');
  const fetchOps = useCallback(() => {
    const params = { clinic_id: id, sort: opSort, order: opOrder, limit: COM_LIMIT, offset: opOffset };
    axios.get(`${API}/admin/operation-invoices`, { headers: { 'X-Admin-Key': key }, params })
      .then(r => { setOpItems(r.data.items); setOpTotal(r.data.total); });
  }, [id, key, opSort, opOrder, opOffset]);
  useEffect(() => { if (tab !== 'op_commissions') return; const t = setTimeout(fetchOps, 250); return () => clearTimeout(t); }, [tab, fetchOps]);
  const opToggleSort = (f) => { setOpOffset(0); if (opSort === f) setOpOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setOpSort(f); setOpOrder('asc'); } };
  const opArrow = (f) => (opSort !== f ? '' : opOrder === 'asc' ? ' ▲' : ' ▼');

  // ── Вкладка «Услуги» (N-103) — что клиника отметила у себя в ЛК, read-only ──
  const [svcItems, setSvcItems] = useState([]);
  const [svcSearch, setSvcSearch] = useState('');
  const [svcSort, setSvcSort] = useState('name');
  const [svcOrder, setSvcOrder] = useState('asc');

  const fetchSvc = useCallback(() => {
    const params = { search: svcSearch || undefined, sort: svcSort, order: svcOrder };
    axios.get(`${API}/admin/clinics/${id}/offered-services`, { headers: { 'X-Admin-Key': key }, params })
      .then(r => setSvcItems(r.data.items));
  }, [id, key, svcSearch, svcSort, svcOrder]);

  useEffect(() => {
    if (tab !== 'services') return;
    const t = setTimeout(fetchSvc, 250);
    return () => clearTimeout(t);
  }, [tab, fetchSvc]);

  const svcToggleSort = (f) => { if (svcSort === f) setSvcOrder(o => o === 'asc' ? 'desc' : 'asc'); else { setSvcSort(f); setSvcOrder('asc'); } };
  const svcArrow = (f) => (svcSort !== f ? '' : svcOrder === 'asc' ? ' ▲' : ' ▼');

  // ── Действия (API/опрос) ──
  const runPoll = async (params, label) => {
    setBusy(true); setActionMsg('');
    try {
      const r = await axios.post(`${API}/admin/run-poll`, null, { headers, params: { clinic_id: id, ...params } });
      const res = r.data.results || [];
      const payments = res.reduce((s, x) => s + (x.payments || 0), 0);
      const confirmed = res.reduce((s, x) => s + (x.confirmed || 0), 0);
      setActionMsg(`${label}: подтверждено ${confirmed}, оплат ${payments}`);
      load(dateFrom, dateTo);
    } catch { setActionMsg('Ошибка запроса к клинике'); } finally { setBusy(false); }
  };
  const checkApi = async () => {
    setBusy(true); setActionMsg('');
    try {
      const r = await axios.post(`${API}/admin/clinics/${id}/check-api`, null, { headers });
      setActionMsg(`Статус API: ${r.data.api_status}`); load(dateFrom, dateTo);
    } catch { setActionMsg('Не удалось проверить API'); } finally { setBusy(false); }
  };
  const sync = () => runPoll({}, 'Синхронизация');
  const backfill = () => {
    const to = new Date(); const from = new Date(Date.now() - 30 * 86400000);
    runPoll({ date_from: isoDay(from), date_to: isoDay(to) }, 'Бэкфилл');
  };
  const doHide = async () => {
    setBusy(true); setHideMsg('');
    try {
      await axios.post(`${API}/admin/clinics/${id}/hide`, null, { headers });
      setHideMsg('Клиника скрыта от врачей'); setShowHideConfirm(false); setHideConfirmName(''); load(dateFrom, dateTo);
    } catch (e) { setHideMsg(e?.response?.data?.detail || 'Не удалось скрыть клинику'); } finally { setBusy(false); }
  };
  const showTarget = async () => {
    setBusy(true); setHideMsg('');
    try {
      await axios.post(`${API}/admin/clinics/${id}/show`, null, { headers });
      setHideMsg('Клиника показана врачам'); load(dateFrom, dateTo);
    } catch (e) { setHideMsg(e?.response?.data?.detail || 'Не удалось показать клинику'); } finally { setBusy(false); }
  };

  // ── Модалки ──
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', api_endpoint: '', api_key: '', address: '', phone: '' });
  const [editErr, setEditErr] = useState('');
  const [showCond, setShowCond] = useState(false);
  const [condForm, setCondForm] = useState({ commission_primary_pct: '', commission_repeat_pct: '', monthly_quota: '', timeout_days: '' });
  const [condErr, setCondErr] = useState('');
  const [showOpCond, setShowOpCond] = useState(false);
  const [opCondForm, setOpCondForm] = useState({ commission_pct: '', timeout_days: '' });
  const [opCondErr, setOpCondErr] = useState('');
  const [showCred, setShowCred] = useState(false);
  const [credForm, setCredForm] = useState({ email: '', password: '' });
  const [credMsg, setCredMsg] = useState('');
  const [showBilling, setShowBilling] = useState(false);
  const [billingForm, setBillingForm] = useState({});
  const [billingErr, setBillingErr] = useState('');

  const saveCred = async () => {
    if (!credForm.email.trim() || !credForm.password) { setCredMsg('Заполните email и пароль'); return; }
    setBusy(true); setCredMsg('');
    try {
      const r = await axios.post(`${API}/admin/clinics/${id}/credentials`, credForm, { headers });
      setCredMsg(r.data.message); setShowCred(false); load(dateFrom, dateTo);
    } catch (e) { setCredMsg(e?.response?.data?.detail || 'Ошибка'); } finally { setBusy(false); }
  };
  const openEdit = () => { setEditForm({ name: data.name || '', api_endpoint: data.api_endpoint || '', api_key: '', address: data.address || '', phone: data.phone || '' }); setEditErr(''); setShowEdit(true); };
  const openBilling = () => { setBillingForm({ ...BILLING_EMPTY, ...(data.billing || {}) }); setBillingErr(''); setShowBilling(true); };
  const saveBilling = async () => {
    setBusy(true); setBillingErr('');
    try {
      await axios.patch(`${API}/admin/clinics/${id}/billing`, billingForm, { headers });
      setShowBilling(false); load(dateFrom, dateTo);
    } catch (e) { setBillingErr(e?.response?.data?.detail || 'Не удалось сохранить реквизиты'); } finally { setBusy(false); }
  };
  const saveClinic = async () => {
    setBusy(true); setEditErr('');
    try {
      const payload = { name: editForm.name, api_endpoint: editForm.api_endpoint, address: editForm.address, phone: editForm.phone };
      if (editForm.api_key.trim()) payload.api_key = editForm.api_key;
      await axios.patch(`${API}/admin/clinics/${id}`, payload, { headers });
      setShowEdit(false); load(dateFrom, dateTo);
    } catch (e) { setEditErr(e?.response?.data?.detail || 'Не удалось сохранить'); } finally { setBusy(false); }
  };
  const openCond = () => {
    const c = data.current_conditions;
    setCondForm({
      commission_primary_pct: c ? c.commission_primary_pct : '',
      commission_repeat_pct: c ? c.commission_repeat_pct : '',
      monthly_quota: c ? c.monthly_quota : '',
      timeout_days: c ? c.timeout_days : '',
    });
    setCondErr(''); setShowCond(true);
  };
  const saveCond = async () => {
    setBusy(true); setCondErr('');
    try {
      await axios.post(`${API}/admin/clinics/${id}/conditions`, {
        commission_primary_pct: Number(condForm.commission_primary_pct),
        commission_repeat_pct: Number(condForm.commission_repeat_pct),
        monthly_quota: Number(condForm.monthly_quota),
        timeout_days: Number(condForm.timeout_days),
      }, { headers });
      setShowCond(false); load(dateFrom, dateTo);
    } catch (e) { setCondErr(e?.response?.data?.detail || 'Не удалось сохранить условия'); } finally { setBusy(false); }
  };
  const openOpCond = () => {
    const c = data.current_operation_conditions;
    setOpCondForm({ commission_pct: c ? c.commission_pct : '', timeout_days: c ? c.timeout_days : '' });
    setOpCondErr(''); setShowOpCond(true);
  };
  const saveOpCond = async () => {
    setBusy(true); setOpCondErr('');
    try {
      await axios.post(`${API}/admin/clinics/${id}/operation-conditions`, {
        commission_pct: Number(opCondForm.commission_pct),
        timeout_days: Number(opCondForm.timeout_days),
      }, { headers });
      setShowOpCond(false); load(dateFrom, dateTo);
    } catch (e) { setOpCondErr(e?.response?.data?.detail || 'Не удалось сохранить условия'); } finally { setBusy(false); }
  };
  const doDisconnect = async () => {
    setBusy(true); setActionMsg('');
    try {
      const r = await axios.post(`${API}/admin/clinics/${id}/disconnect`, null, { headers, params: { confirm_name: confirmName } });
      setActionMsg(r.data.message || 'Клиника отключена'); setShowDisconnect(false); setConfirmName(''); load(dateFrom, dateTo);
    } catch (e) { setActionMsg(e?.response?.data?.detail || 'Не удалось отключить клинику'); } finally { setBusy(false); }
  };

  if (error) return <Layout><div style={styles.empty}>{error}</div></Layout>;
  if (!data) return <Layout><div style={styles.empty}>Загрузка…</div></Layout>;

  const cond = data.current_conditions;
  const sched = data.scheduled_conditions;
  const opCond = data.current_operation_conditions;
  const opSched = data.scheduled_operation_conditions;
  // Клиника-член сети без своего API endpoint — опрос идёт по сети целиком,
  // у самой клиники нечего проверять/синхронизировать (see N-024).
  const noOwnApi = !!data.network_id && !data.api_endpoint;

  const TabBtn = ({ id: tid, label }) => (
    <div style={{ ...styles.tab, ...(tab === tid ? styles.tabActive : {}) }} onClick={() => setTab(tid)}>{label}</div>
  );

  return (
    <Layout>
      <div style={styles.back} onClick={() => navigate('/clinics')}>← К списку клиник</div>

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{data.name}</h1>
          <div style={styles.sub}><span style={styles.clinicId}>{formatClinicId(data.id)}</span></div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {!data.is_active
              ? <span style={styles.badgeRed}>Отключена</span>
              : (!data.network_id && data.is_hidden)
              ? <span style={styles.badgeOrange}>Скрыто</span>
              : (data.network_id && data.network_is_hidden)
              ? <span style={styles.badgeOrange}>Скрыто (сеть)</span>
              : <span style={styles.badgeGreen}>Активна</span>}
            <StatusInfo def="clinic_active" />
            {noOwnApi ? (
              <span style={styles.badgeGray}>API — на уровне сети</span>
            ) : (
              <>
                {data.api_status === 'active' ? <span style={styles.badgeGreen}>API активно</span>
                  : data.api_status === 'failed' ? <span style={styles.badgeRed}>API недоступно</span>
                    : <span style={styles.badgeGray}>API не проверено</span>}
                <StatusInfo def="api" />
              </>
            )}
          </div>
          {!noOwnApi && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button style={styles.btnGhost} onClick={checkApi} disabled={busy}>Проверить API</button>
              <button style={styles.btn} onClick={sync} disabled={busy} title="Запросить клинику сейчас, не дожидаясь ежедневного опроса">⟳ Синхронизация</button>
              <button style={styles.btnGhost} onClick={backfill} disabled={busy}>Бэкфилл</button>
              <span style={styles.hint}>дозагрузка за 30 дней</span>
            </div>
          )}
          {actionMsg && <div style={styles.actionMsg}>{busy ? 'Выполняется…' : actionMsg}</div>}
        </div>
      </div>

      {!noOwnApi && data.poll_error_count > 0 && (
        <div style={styles.pollErrorBanner}>
          ⚠ Опрос API не проходит: <b>{data.poll_error_count} дн. подряд</b>.
          {data.last_poll_error_at && <> Последняя ошибка: {new Date(data.last_poll_error_at).toLocaleString('ru')}.</>}
          {' '}Проверьте доступность API клиники (кнопка «Проверить API»).
        </div>
      )}
      {data.disconnected_at && (
        <div style={styles.disconnectBanner}>
          Договор расторгнут {new Date(data.disconnected_at).toLocaleDateString('ru')}.
          Обязательства (выплаты по повторным, квота 0) — до{' '}
          <b>{data.obligations_end_at ? new Date(data.obligations_end_at).toLocaleDateString('ru') : '—'}</b>.
        </div>
      )}

      {/* Вкладки: у клиники-члена сети направления/комиссии всегда пусты (target — сеть, не она) */}
      <div style={styles.tabs}>
        <TabBtn id="overview" label="Обзор" />
        {!data.network_id && <TabBtn id="referrals" label="Направления" />}
        {!data.network_id && <TabBtn id="commissions" label="Комиссии по врачам и месяцам" />}
        {!data.network_id && <TabBtn id="op_commissions" label="Комиссии по операциям" />}
        {!data.network_id && <TabBtn id="services" label="Услуги" />}
      </div>

      {/* ── ОБЗОР ── */}
      {tab === 'overview' && (
        <>
          <div style={styles.h2row}>
            <h2 style={styles.h2}>Подключение</h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {credMsg && <span style={styles.actionMsg}>{credMsg}</span>}
              {!data.network_id && (
                <>
                  <button style={styles.btn} onClick={impersonate} disabled={impersonateBusy} title="Открыть ЛК клиники в новой вкладке от её лица (для разбора обращений в ТП)">
                    {impersonateBusy ? 'Открываем…' : 'Войти в кабинет'}
                  </button>
                  <button style={styles.btnGhost} onClick={() => { setCredForm({ email: data.email || '', password: '' }); setCredMsg(''); setShowCred(true); }}>
                    {data.email ? `Логин ЛК: ${data.email}` : 'Выдать логин ЛК'}
                  </button>
                </>
              )}
              <button style={styles.btnGhost} onClick={openBilling}>Реквизиты (для счёта)</button>
              <button style={styles.btnGhost} onClick={openEdit}>Редактировать</button>
            </div>
          </div>
          <div style={styles.card}>
            <div style={styles.grid}>
              <Field label="Адрес" value={data.address || '—'} />
              <Field label="Телефон" value={data.phone || '—'} />
              <Field label="API endpoint" value={data.api_endpoint || '—'} />
              <Field label="Подключена" value={new Date(data.created_at).toLocaleDateString('ru')} />
              <Field label="Отключена" value={data.disconnected_at ? new Date(data.disconnected_at).toLocaleDateString('ru') : '—'} />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={styles.h2}>Условия</h2>
            {!data.disconnected_at && !data.network_id && <button style={styles.btnGhost} onClick={openCond}>Изменить условия</button>}
          </div>
          <div style={styles.card}>
            {data.network_id ? (
              <div style={styles.muted}>
                Условия задаются на уровне сети —{' '}
                <span style={{ color: '#2EC4B6', cursor: 'pointer' }} onClick={() => navigate(`/networks/${data.network_id}`)}>{data.network_name || 'перейти к сети'}</span>
              </div>
            ) : cond ? (
              <div style={styles.grid}>
                <Field label="Первичная комиссия" value={`${cond.commission_primary_pct}%`} />
                <Field label="Повторная комиссия" value={`${cond.commission_repeat_pct}%`} />
                <Field label="Месячная квота" value={cond.monthly_quota} />
                <Field label="Таймер (дней)" value={cond.timeout_days} />
                <Field label="Действуют с" value={new Date(cond.effective_from).toLocaleDateString('ru')} />
              </div>
            ) : <div style={styles.muted}>Условия ещё не заданы</div>}
            {!data.network_id && sched && (
              <div style={{ ...styles.grid, marginTop: 16, paddingTop: 16, borderTop: '1px solid #334155' }}>
                <Field label="Запланировано: первичная" value={`${sched.commission_primary_pct}%`} />
                <Field label="Запланировано: повторная" value={`${sched.commission_repeat_pct}%`} />
                <Field label="Запланировано: квота" value={sched.monthly_quota} />
                <Field label="Запланировано: таймер" value={`${sched.timeout_days} дн.`} />
                <Field label="Вступит в силу" value={new Date(sched.effective_from).toLocaleDateString('ru')} />
              </div>
            )}
          </div>

          {!data.network_id && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={styles.h2}>Условия «Операций»<StatusInfo def="operation_conditions" /></h2>
                {!data.disconnected_at && <button style={styles.btnGhost} onClick={openOpCond}>Изменить условия</button>}
              </div>
              <div style={styles.card}>
                {opCond ? (
                  <div style={styles.grid}>
                    <Field label="Комиссия" value={`${opCond.commission_pct}%`} />
                    <Field label="Период (дней)" value={opCond.timeout_days} />
                    <Field label="Действуют с" value={new Date(opCond.effective_from).toLocaleDateString('ru')} />
                  </div>
                ) : <div style={styles.muted}>Не заданы — цель не видна врачам в списке «Добавить на операцию»</div>}
                {opSched && (
                  <div style={{ ...styles.grid, marginTop: 16, paddingTop: 16, borderTop: '1px solid #334155' }}>
                    <Field label="Запланировано: комиссия" value={`${opSched.commission_pct}%`} />
                    <Field label="Запланировано: период" value={`${opSched.timeout_days} дн.`} />
                    <Field label="Вступит в силу" value={new Date(opSched.effective_from).toLocaleDateString('ru')} />
                  </div>
                )}
              </div>
            </>
          )}

          {data.network_id ? (
            <div style={styles.card}>
              <div style={styles.muted}>
                Направления, транзакции и комиссии считаются на уровне сети — врачи направляют пациентов
                в сеть в целом, а не в это конкретное учреждение.{' '}
                <span style={{ color: '#2EC4B6', cursor: 'pointer' }} onClick={() => navigate(`/networks/${data.network_id}`)}>
                  Перейти к сети «{data.network_name}» →
                </span>
              </div>
            </div>
          ) : (
            <>
              <div style={styles.filterBar}>
                <button style={styles.btnGhost} onClick={reset}>Всё время</button>
                <input type="date" style={styles.dateInput} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                <span style={{ color: '#64748b' }}>—</span>
                <input type="date" style={styles.dateInput} value={dateTo} onChange={e => setDateTo(e.target.value)} />
                <button style={styles.btn} onClick={apply} disabled={!dateFrom || !dateTo}>Применить</button>
              </div>

              <div style={styles.metrics}>
                <Metric label="Направлений" value={data.referrals_count} />
                <Metric label="Транзакций" value={data.transactions_count} />
                <Metric label="Комиссий врачам" value={`${fmtMoney(data.total_commissions)} ₽`} color="#2EC4B6" />
                <Metric label="Доход Refero" value={`${fmtMoney(data.total_platform_fee)} ₽`} />
              </div>
            </>
          )}
          {!data.disconnected_at && !data.network_id && (
            <div style={styles.dangerZone}>
              {hideMsg && <span style={styles.actionMsg}>{busy ? 'Выполняется…' : hideMsg}</span>}
              {data.is_active && (
                data.is_hidden
                  ? <button style={styles.btnOrange} onClick={showTarget} disabled={busy}>Показать врачам</button>
                  : <button style={styles.btnOrange} onClick={() => setShowHideConfirm(true)} disabled={busy}>Скрыть от врачей</button>
              )}
              <button style={styles.btnDanger} onClick={() => setShowDisconnect(true)} disabled={busy}>Отключить от Refero</button>
            </div>
          )}
        </>
      )}

      {/* ── НАПРАВЛЕНИЯ ── */}
      {tab === 'referrals' && (
        <>
          <div style={styles.filterBar}>
            <input style={styles.searchInput} placeholder="Поиск по пациенту / врачу / номеру направления" value={refSearch}
              onChange={e => { setRefSearch(e.target.value); setRefOffset(0); }} />
            <select style={styles.select} value={refStatus} onChange={e => { setRefStatus(e.target.value); setRefOffset(0); }}>
              {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div style={styles.table}>
            <div style={{ ...styles.thead, gridTemplateColumns: refCols }}>
              <div style={styles.th}>ID</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => refToggleSort('patient_name')}>Пациент{refArrow('patient_name')}</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => refToggleSort('doctor_name')}>Врач{refArrow('doctor_name')}</div>
              <div style={styles.th}>ID врача</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => refToggleSort('status')}>Статус{refArrow('status')}<StatusInfo def="referral" /></div>
              <div style={styles.th}>Дата $ заявлено<StatusInfo def="duplicate_first_payment" /></div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => refToggleSort('bound_at')}>Привязан{refArrow('bound_at')}</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => refToggleSort('primary_visit_at')}>Первичный визит{refArrow('primary_visit_at')}</div>
            </div>
            {refItems.map(r => (
              <div key={r.id} style={{ ...styles.row, gridTemplateColumns: refCols }}>
                <div style={{ ...styles.td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#64748b', fontSize: 12 }}>REF-{r.id}</div>
                <div style={{ ...styles.td, ...styles.link }} onClick={() => navigate(`/patients/${r.patient_id}`)}>{r.patient_name}</div>
                <div style={{ ...styles.td, ...styles.link }} onClick={() => navigate(`/doctors/${r.doctor_id}`)}>{r.doctor_name}</div>
                <div style={{ ...styles.td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#64748b', fontSize: 12 }}>{r.doctor_public_id || '—'}</div>
                <div style={styles.td}>{referralLabel(r.status, r.clinic_confirmed_at)}</div>
                <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>{r.status === 'duplicate' && r.duplicate_first_payment_date ? new Date(r.duplicate_first_payment_date).toLocaleDateString('ru') : '—'}</div>
                <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>{new Date(r.bound_at).toLocaleDateString('ru')}</div>
                <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>{r.primary_visit_at ? new Date(r.primary_visit_at).toLocaleDateString('ru') : '—'}</div>
              </div>
            ))}
            {refItems.length === 0 && <div style={styles.empty}>Направлений не найдено</div>}
          </div>
          <ListPager total={refTotal} limit={REF_LIMIT} offset={refOffset} onPage={setRefOffset} />
        </>
      )}

      {/* ── КОМИССИИ ── */}
      {tab === 'commissions' && (
        <>
          <div style={styles.filterBar}>
            <input style={styles.searchInput} placeholder="Поиск по врачу" value={comSearch}
              onChange={e => { setComSearch(e.target.value); setComOffset(0); }} />
            <select style={styles.select} value={comMonth} onChange={e => { setComMonth(e.target.value); setComOffset(0); }}>
              {MONTH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div style={styles.table}>
            <div style={{ ...styles.thead, gridTemplateColumns: comCols }}>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => comToggleSort('period')}>Период{comArrow('period')}</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => comToggleSort('doctor')}>Врач{comArrow('doctor')}</div>
              <div style={styles.th}>Первичные</div>
              <div style={styles.th}>Повторные</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => comToggleSort('subtotal')}>Итого{comArrow('subtotal')}</div>
              <div style={styles.th}>Refero</div>
              <div style={styles.th}>Квота</div>
              <div style={styles.th}>Выплата<StatusInfo def="payment" /></div>
              <div style={styles.th}>Оплата клиникой<StatusInfo def="clinic_invoice" /></div>
            </div>
            {comItems.map((c, i) => (
              <div key={i} style={{ ...styles.row, gridTemplateColumns: comCols }}>
                <div style={styles.td}>{MONTHS[c.period_month]} {c.period_year}</div>
                <div style={{ ...styles.td, ...styles.link }} onClick={() => navigate(`/doctors/${c.doctor_id}`)}>{c.doctor_name}</div>
                <div style={styles.td}>{fmtMoney(c.primary_amount)} ₽</div>
                <div style={styles.td}>{fmtMoney(c.repeat_amount)} ₽</div>
                <div style={{ ...styles.td, color: '#2EC4B6', fontWeight: 600 }}>{fmtMoney(c.subtotal)} ₽</div>
                <div style={styles.td}>{fmtMoney(c.platform_fee)} ₽</div>
                <div style={styles.td}>
                  {c.quota_fulfilled
                    ? <span style={styles.badgeGreen}>✅ {c.quota_count}{c.quota_min != null ? `/${c.quota_min}` : ''}</span>
                    : <span style={styles.badgeGray}>⏳ {c.quota_count}{c.quota_min != null ? `/${c.quota_min}` : ''}</span>}
                </div>
                <div style={styles.td}>{c.is_paid ? <span style={styles.badgeGreen}>Выплачено</span> : <span style={styles.badgeGray}>Ожидает</span>}</div>
                <div style={styles.td}>
                  {c.invoice_status === 'paid' ? <span style={styles.badgeGreen}>Оплачено</span>
                    : c.invoice_status === 'invoiced' ? <span style={styles.badgeYellow}>Ожидает</span>
                    : <span style={styles.badgeGray}>Не оплачено</span>}
                </div>
              </div>
            ))}
            {comItems.length === 0 && <div style={styles.empty}>Комиссий не найдено</div>}
          </div>
          <ListPager total={comTotal} limit={COM_LIMIT} offset={comOffset} onPage={setComOffset} />
        </>
      )}

      {/* ── КОМИССИИ ПО ОПЕРАЦИЯМ (N-119) ── */}
      {tab === 'op_commissions' && (
        <>
          <div style={styles.table}>
            <div style={{ ...styles.thead, gridTemplateColumns: opCols }}>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => opToggleSort('doctor_name')}>Врач{opArrow('doctor_name')}</div>
              <div style={styles.th}>Номер направления</div>
              <div style={styles.th}>Период</div>
              <div style={styles.th}>Сумма оплат</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => opToggleSort('total_amount')}>Врачу{opArrow('total_amount')}</div>
              <div style={styles.th}>Refero</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => opToggleSort('status')}>Счёт{opArrow('status')}<StatusInfo def="operation_invoice" /></div>
            </div>
            {opItems.map(row => (
              <div key={row.referral_id} style={{ ...styles.row, gridTemplateColumns: opCols }}>
                <div style={{ ...styles.td, ...(row.doctor_id ? styles.link : {}) }} onClick={() => row.doctor_id && navigate(`/doctors/${row.doctor_id}`)}>
                  {row.doctor_name}
                </div>
                <div style={{ ...styles.td, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: '#64748b', fontSize: 12 }}>REF-{row.referral_id}</div>
                <div style={{ ...styles.td, color: '#94a3b8', fontSize: 12 }}>{row.window_start} – {row.window_end}</div>
                <div style={styles.td}>{fmtMoney(row.total_amount)} ₽ <span style={{ color: '#475569', fontSize: 12, marginLeft: 6 }}>({row.commission_pct}%)</span></div>
                <div style={{ ...styles.td, color: '#2EC4B6', fontWeight: 700 }}>{fmtMoney(row.doctor_amount)} ₽</div>
                <div style={{ ...styles.td, color: '#f59e0b' }}>{fmtMoney(row.platform_fee)} ₽</div>
                <div style={styles.td}>{row.status === 'paid' ? <span style={styles.badgeGreen}>Оплачено</span> : <span style={styles.badgeGray}>Ожидает оплаты</span>}</div>
              </div>
            ))}
            {opItems.length === 0 && <div style={styles.empty}>Закрытых операционных направлений не найдено</div>}
          </div>
          <ListPager total={opTotal} limit={COM_LIMIT} offset={opOffset} onPage={setOpOffset} />
        </>
      )}

      {/* ── УСЛУГИ (N-103) ── */}
      {tab === 'services' && (
        <>
          <div style={styles.hint}>
            Что клиника отметила у себя как доступную услугу (из общего справочника Refero —
            страница «Услуги клиник» в меню). Редактирует сама клиника/сеть в своём ЛК —
            здесь только просмотр.
          </div>
          <div style={styles.filterBar}>
            <input style={styles.searchInput} placeholder="Поиск по услуге" value={svcSearch}
              onChange={e => setSvcSearch(e.target.value)} />
          </div>
          <div style={styles.table}>
            <div style={{ ...styles.thead, gridTemplateColumns: svcCols }}>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => svcToggleSort('name')}>Услуга{svcArrow('name')}</div>
              <div style={{ ...styles.th, ...styles.thClickable }} onClick={() => svcToggleSort('created_at')}>Добавлено{svcArrow('created_at')}</div>
            </div>
            {svcItems.map(s => (
              <div key={s.id} style={{ ...styles.row, gridTemplateColumns: svcCols }}>
                <div style={styles.td}>{s.name}</div>
                <div style={{ ...styles.td, color: '#64748b', fontSize: 12 }}>{new Date(s.added_at).toLocaleDateString('ru')}</div>
              </div>
            ))}
            {svcItems.length === 0 && <div style={styles.empty}>Клиника пока не отметила ни одной услуги</div>}
          </div>
        </>
      )}

      {/* ── Модалки ── */}
      {showHideConfirm && (
        <div style={styles.modalOverlay} onClick={() => { setShowHideConfirm(false); setHideConfirmName(''); }}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Скрыть клинику от врачей?</h3>
            <p style={styles.modalText}>
              Клиника перестанет быть доступна как цель для новых направлений в кабинете
              врача. Обратимо в любой момент кнопкой «Показать врачам».
              <br /><br />Для подтверждения введите точное название клиники:
              <br /><b style={{ color: '#e2e8f0' }}>{data.name}</b>
            </p>
            <input style={styles.modalInput} placeholder="Название клиники" value={hideConfirmName} onChange={e => setHideConfirmName(e.target.value)} autoFocus />
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => { setShowHideConfirm(false); setHideConfirmName(''); }}>Отмена</button>
              <button style={{ ...styles.btnOrange, ...(hideConfirmName.trim() === data.name.trim() ? {} : styles.btnDisabled) }}
                onClick={doHide} disabled={busy || hideConfirmName.trim() !== data.name.trim()}>Скрыть</button>
            </div>
          </div>
        </div>
      )}
      {showDisconnect && (
        <div style={styles.modalOverlay} onClick={() => setShowDisconnect(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Отключить клинику от Refero?</h3>
            <p style={styles.modalText}>
              Действие необратимо. Начнётся 6-месячный период обязательств: повторные оплаты
              продолжат начисляться (квота 0), новые направления в клинику будут недоступны.
              <br /><br />Для подтверждения введите точное название клиники:
              <br /><b style={{ color: '#e2e8f0' }}>{data.name}</b>
            </p>
            <input style={styles.modalInput} placeholder="Название клиники" value={confirmName} onChange={e => setConfirmName(e.target.value)} autoFocus />
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => { setShowDisconnect(false); setConfirmName(''); }}>Отмена</button>
              <button style={{ ...styles.btnDanger, ...(confirmName.trim() === data.name.trim() ? {} : styles.btnDisabled) }}
                onClick={doDisconnect} disabled={busy || confirmName.trim() !== data.name.trim()}>Отключить</button>
            </div>
          </div>
        </div>
      )}
      {showEdit && (
        <div style={styles.modalOverlay} onClick={() => setShowEdit(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Редактировать клинику</h3>
            <div style={styles.fieldLabel}>Название</div>
            <input style={styles.modalInput} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
            <div style={{ ...styles.fieldLabel, marginTop: 10 }}>Адрес</div>
            <input style={styles.modalInput} value={editForm.address} onChange={e => setEditForm({ ...editForm, address: e.target.value })} />
            <div style={{ ...styles.fieldLabel, marginTop: 10 }}>Телефон</div>
            <input style={styles.modalInput} value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} />
            <div style={{ ...styles.fieldLabel, marginTop: 10 }}>API endpoint</div>
            <input style={styles.modalInput} value={editForm.api_endpoint} onChange={e => setEditForm({ ...editForm, api_endpoint: e.target.value })} />
            <div style={{ ...styles.fieldLabel, marginTop: 10 }}>API key (оставьте пустым — не менять)</div>
            <input style={styles.modalInput} placeholder="••••••" value={editForm.api_key} onChange={e => setEditForm({ ...editForm, api_key: e.target.value })} />
            {editErr && <div style={styles.errText}>{editErr}</div>}
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => setShowEdit(false)}>Отмена</button>
              <button style={styles.btn} onClick={saveClinic} disabled={busy}>Сохранить</button>
            </div>
          </div>
        </div>
      )}
      {showCred && (
        <div style={styles.modalOverlay} onClick={() => setShowCred(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Логин ЛК клиники</h3>
            <div style={styles.modalText}>Учётка для входа клиники в её личный кабинет.</div>
            <div style={styles.fieldLabel}>Email</div>
            <input style={styles.modalInput} value={credForm.email} onChange={e => setCredForm({ ...credForm, email: e.target.value })} />
            <div style={{ ...styles.fieldLabel, marginTop: 10 }}>Пароль</div>
            <input style={styles.modalInput} type="text" value={credForm.password} onChange={e => setCredForm({ ...credForm, password: e.target.value })} />
            {credMsg && <div style={styles.errText}>{credMsg}</div>}
            <div style={styles.modalActions}><button style={styles.btnGhost} onClick={() => setShowCred(false)}>Отмена</button><button style={styles.btn} onClick={saveCred} disabled={busy}>Сохранить</button></div>
          </div>
        </div>
      )}
      {showCond && (
        <div style={styles.modalOverlay} onClick={() => setShowCond(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Условия клиники</h3>
            <div style={styles.modalText}>Первая установка действует сразу. Изменение вступит в силу с 1 числа следующего месяца. Нельзя снижать % и таймер, повышать квоту.</div>
            <div style={styles.fieldLabel}>Первичная комиссия, %</div>
            <input style={styles.modalInput} type="number" value={condForm.commission_primary_pct} onChange={e => setCondForm({ ...condForm, commission_primary_pct: e.target.value })} />
            <div style={{ ...styles.fieldLabel, marginTop: 10 }}>Повторная комиссия, %</div>
            <input style={styles.modalInput} type="number" value={condForm.commission_repeat_pct} onChange={e => setCondForm({ ...condForm, commission_repeat_pct: e.target.value })} />
            <div style={{ ...styles.fieldLabel, marginTop: 10 }}>Месячная квота</div>
            <input style={styles.modalInput} type="number" value={condForm.monthly_quota} onChange={e => setCondForm({ ...condForm, monthly_quota: e.target.value })} />
            <div style={{ ...styles.fieldLabel, marginTop: 10 }}>Таймер, дней (срок на оплату визита с момента привязки)</div>
            <input style={styles.modalInput} type="number" value={condForm.timeout_days} onChange={e => setCondForm({ ...condForm, timeout_days: e.target.value })} />
            {condErr && <div style={styles.errText}>{condErr}</div>}
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => setShowCond(false)}>Отмена</button>
              <button style={styles.btn} onClick={saveCond} disabled={busy}>Сохранить</button>
            </div>
          </div>
        </div>
      )}
      {showOpCond && (
        <div style={styles.modalOverlay} onClick={() => setShowOpCond(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Условия «Операций»</h3>
            <div style={styles.modalText}>Первая установка действует сразу. Изменение вступит в силу с 1 числа следующего месяца. Нельзя снижать % или сокращать период.</div>
            <div style={styles.fieldLabel}>Комиссия, % от суммы всех оплат за период</div>
            <input style={styles.modalInput} type="number" value={opCondForm.commission_pct} onChange={e => setOpCondForm({ ...opCondForm, commission_pct: e.target.value })} />
            <div style={{ ...styles.fieldLabel, marginTop: 10 }}>Период, дней (с момента направления)</div>
            <input style={styles.modalInput} type="number" value={opCondForm.timeout_days} onChange={e => setOpCondForm({ ...opCondForm, timeout_days: e.target.value })} />
            {opCondErr && <div style={styles.errText}>{opCondErr}</div>}
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => setShowOpCond(false)}>Отмена</button>
              <button style={styles.btn} onClick={saveOpCond} disabled={busy}>Сохранить</button>
            </div>
          </div>
        </div>
      )}
      {showBilling && (
        <div style={styles.modalOverlay} onClick={() => setShowBilling(false)}>
          <div style={{ ...styles.modal, width: 480 }} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Реквизиты клиники</h3>
            <div style={styles.modalText}>Используются в шапке PDF-счёта, который выставляется клинике.</div>
            {BILLING_FIELDS.map(([field, label]) => (
              <div key={field}>
                <div style={styles.fieldLabel}>{label}</div>
                <input style={styles.modalInput} value={billingForm[field] || ''}
                  onChange={e => setBillingForm({ ...billingForm, [field]: e.target.value })} />
              </div>
            ))}
            {billingErr && <div style={styles.errText}>{billingErr}</div>}
            <div style={styles.modalActions}>
              <button style={styles.btnGhost} onClick={() => setShowBilling(false)}>Отмена</button>
              <button style={styles.btn} onClick={saveBilling} disabled={busy}>Сохранить</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Field({ label, value }) {
  return (<div><div style={styles.fieldLabel}>{label}</div><div style={styles.fieldValue}>{value}</div></div>);
}
function Metric({ label, value, color }) {
  return (<div style={styles.metric}><div style={styles.metricLabel}>{label}</div><div style={{ ...styles.metricValue, ...(color ? { color } : {}) }}>{value}</div></div>);
}

const refCols = '0.8fr 2fr 1.6fr 1fr 1.2fr 1.1fr 1fr 1fr';
const comCols = '1.5fr 1.6fr 1fr 1fr 1fr 1fr 1fr 1.2fr 1.3fr';
const opCols = '1.8fr 1.1fr 1.3fr 1.3fr 1fr 1fr 1.2fr';
const svcCols = '3fr 1fr';

const styles = {
  back: { color: '#64748b', fontSize: 13, cursor: 'pointer', marginBottom: 16 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { color: 'white', fontSize: 24, fontWeight: 700, margin: 0 },
  sub: { marginTop: 8 },
  clinicId: { fontFamily: 'monospace', fontSize: 13, color: '#94a3b8', background: '#1e293b', padding: '2px 8px', borderRadius: 6, border: '1px solid #334155' },
  tabs: { display: 'flex', gap: 4, borderBottom: '1px solid #334155', marginBottom: 24 },
  tab: { padding: '10px 18px', color: '#94a3b8', fontSize: 14, fontWeight: 600, cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -1 },
  tabActive: { color: '#2EC4B6', borderBottom: '2px solid #2EC4B6' },
  card: { background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 24 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 },
  fieldLabel: { fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  fieldValue: { fontSize: 14, color: '#e2e8f0', wordBreak: 'break-all' },
  muted: { color: '#64748b', fontSize: 14 },
  hint: { color: '#64748b', fontSize: 12 },
  actionMsg: { color: '#2EC4B6', fontSize: 12 },
  btnDanger: { background: '#450a0a', color: '#f87171', border: '1px solid #7f1d1d', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnDisabled: { opacity: 0.4, cursor: 'default' },
  dangerZone: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 40, paddingTop: 20, borderTop: '1px solid #1e293b' },
  btnOrange: { background: '#451a03', color: '#fb923c', border: '1px solid #7c2d12', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  disconnectBanner: { background: '#450a0a', color: '#fca5a5', border: '1px solid #7f1d1d', borderRadius: 10, padding: '12px 16px', fontSize: 13, marginBottom: 20 },
  pollErrorBanner: { background: '#422006', color: '#fcd34d', border: '1px solid #854d0e', borderRadius: 10, padding: '12px 16px', fontSize: 13, marginBottom: 20 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  modal: { background: '#1e293b', borderRadius: 12, padding: 24, width: 440, maxWidth: '90vw', border: '1px solid #334155' },
  modalTitle: { color: 'white', fontSize: 18, fontWeight: 700, margin: '0 0 12px' },
  modalText: { color: '#94a3b8', fontSize: 13, lineHeight: 1.5, margin: '0 0 16px' },
  modalInput: { width: '100%', boxSizing: 'border-box', background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '9px 12px', fontSize: 14, outline: 'none', marginBottom: 16 },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 },
  errText: { color: '#f87171', fontSize: 13, marginTop: 12 },
  filterBar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 },
  searchInput: { background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '7px 12px', fontSize: 13, outline: 'none', minWidth: 260 },
  select: { background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '7px 12px', fontSize: 13, outline: 'none', cursor: 'pointer' },
  dateInput: { background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 6, padding: '6px 10px', fontSize: 13, outline: 'none' },
  btn: { background: '#2EC4B6', color: '#0f172a', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer' },
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 8 },
  metric: { background: '#1e293b', borderRadius: 12, padding: 20 },
  metricLabel: { fontSize: 13, color: '#64748b', marginBottom: 8 },
  metricValue: { fontSize: 24, fontWeight: 700, color: '#e2e8f0' },
  note: { color: '#64748b', fontSize: 12, marginBottom: 24 },
  h2: { color: 'white', fontSize: 18, fontWeight: 600, margin: '8px 0 12px' },
  h2row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  table: { background: '#1e293b', borderRadius: 12, overflow: 'hidden' },
  thead: { display: 'grid', padding: '12px 20px', borderBottom: '1px solid #334155' },
  th: { fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 },
  thClickable: { cursor: 'pointer', userSelect: 'none' },
  row: { display: 'grid', padding: '14px 20px', borderBottom: '1px solid #1e293b', background: '#0f172a', margin: '1px 0' },
  td: { fontSize: 14, color: '#e2e8f0', display: 'flex', alignItems: 'center' },
  link: { color: '#2EC4B6', cursor: 'pointer' },
  empty: { color: '#64748b', fontSize: 14, textAlign: 'center', padding: '32px 0' },
  badgeGreen: { background: '#052e16', color: '#4ade80', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  badgeGray: { background: '#1e293b', color: '#64748b', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block', border: '1px solid #334155' },
  badgeOrange: { background: '#451a03', color: '#fb923c', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  badgeRed: { background: '#450a0a', color: '#f87171', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
  badgeYellow: { background: '#422006', color: '#fbbf24', padding: '3px 8px', borderRadius: 20, fontSize: 12, display: 'inline-block' },
};
